import { inject, Injectable } from '@angular/core';
import { SadmEntityService } from '@modules/sadm/services/sadm-entity.service';
import { buildSadmEntityDraft } from '@modules/sadm/utils/build-sadm-entity-draft';
import { DEFAULT_SADM_ENTITY_NAME_KEY } from '@modules/sadm/utils/default-sadm-entity-name-key';
import { Patient, SadmEntity } from '@pixacare/domain';
import { appendPatientToCreateSequenceFormData, SadmClient, SadmEntityResponse } from '@pixacare/dtos';
import { FormAnswer } from '@pixacare/forms';
import { formatToIso } from '@pixacare/shared';
import { SadmHttpService } from '@services/http/sadm.http.service';
import { SequenceHttpService } from '@services/http/sequence.http.service';
// TODO(cross-sequence-medias): drop import when backend supports attaching existing Media to a different wound
import { TranslocoService } from '@jsverse/transloco';
import { cloneMediaAsFile } from '@modules/shared/media-picker/utils/clone-media-as-file.adapter';
import { catchError, concat, EMPTY, forkJoin, from, map, merge, mergeMap, Observable, of } from 'rxjs';
import { DraftSadmEntityChangeset } from '../models/draft-sadm-entity-changeset';
import { SadmEntityAnalysisChangeset } from '../models/sadm-entity-analysis-changeset';
import { SadmEntityChangeset } from '../models/sadm-entity-changeset';
import { SadmEntityChangesetPartial } from '../models/sadm-entity-changeset-partial';
import { isDraftSadmEntityChangeset } from '../models/sadm-entity-changeset.utils';
import { SavedSadmEntityChangeset } from '../models/saved-sadm-entity-changeset';

const CHANGESET_SAVE_CONCURRENCY = 2;

@Injectable()
export class SadmGridSaveService {
  private readonly sadmHttp = inject(SadmHttpService);
  private readonly sequenceHttp = inject(SequenceHttpService);
  private readonly translocoService = inject(TranslocoService);

  save({
    sadmClient,
    patient,
    changesets,
  }: {
    sadmClient: SadmClient;
    patient: Patient;
    changesets: SadmEntityChangeset[];
  }): Observable<SadmEntityChangesetPartial> {
    if (!changesets.length) {
      return EMPTY;
    }
    return from(changesets).pipe(
      mergeMap((changeset) => this.saveChangeset(changeset, sadmClient, patient), CHANGESET_SAVE_CONCURRENCY),
    );
  }

  saveChangeset(
    changeset: SadmEntityChangeset,
    sadmClient: SadmClient,
    patient: Patient,
  ): Observable<SadmEntityChangesetPartial> {
    if (isDraftSadmEntityChangeset(changeset)) {
      return this.createEntity(changeset, sadmClient, patient).pipe(
        mergeMap((partial) => {
          if (!partial.success) {
            return of(partial);
          }
          // Server returned a real id. The grid entry keeps its stable `key`, so the
          // remaining ops (protocol/analysis) route to the same column — only the HTTP
          // target switches to the freshly created id. `creationAnswer` is dropped: the
          // creation write just happened, re-running it would duplicate it.
          const remaining: SavedSadmEntityChangeset = {
            ...changeset,
            sadmEntityId: partial.sadmEntity.id,
            creationAnswer: undefined,
          };
          return concat(of(partial), this.saveEntityData(remaining, sadmClient, patient));
        }),
      );
    }
    return this.saveEntityData(changeset, sadmClient, patient);
  }

  private saveEntityData(
    changeset: SavedSadmEntityChangeset,
    sadmClient: SadmClient,
    patient: Patient,
  ): Observable<SadmEntityChangesetPartial> {
    const ops: Observable<SadmEntityChangesetPartial>[] = [];

    if (changeset.creationAnswer) {
      const name = SadmEntityService.getNewSadmEntityName({
        sadmClient,
        currentName: changeset.currentName ?? '',
        currentAnswer: changeset.currentCreationAnswer ?? { answers: {} },
        newAnswer: changeset.creationAnswer,
      });
      ops.push(this.creationOp(changeset.key, changeset.sadmEntityId, changeset.creationAnswer, name));
    }
    if (changeset.protocolAnswer) {
      ops.push(this.protocolOp(changeset.key, changeset.sadmEntityId, sadmClient.id, changeset.protocolAnswer));
    }
    if (changeset.analysis) {
      ops.push(this.analysisOps(changeset.key, changeset.sadmEntityId, changeset.analysis, patient));
    }
    return merge(...ops);
  }

  private createEntity(
    changeset: DraftSadmEntityChangeset,
    sadmClient: SadmClient,
    patient: Patient,
  ): Observable<Extract<SadmEntityChangesetPartial, { action: 'creation' }>> {
    const key = changeset.key;
    const creationAnswer: FormAnswer = changeset.creationAnswer;
    const draft = buildSadmEntityDraft(sadmClient, {
      patientId: patient.id,
      clientCode: patient.clientCode ?? '',
      answer: creationAnswer,
      fallbackName: this.translocoService.translate(DEFAULT_SADM_ENTITY_NAME_KEY),
    });
    type CreationPartial = Extract<SadmEntityChangesetPartial, { action: 'creation' }>;
    return this.sadmHttp.createSadmEntity(draft).pipe(
      map((sadmEntity: SadmEntityResponse): CreationPartial => ({
        action: 'creation',
        success: true,
        key,
        answer: creationAnswer,
        sadmEntity,
      })),
      catchError((error: unknown): Observable<CreationPartial> =>
        of({ action: 'creation', success: false, key, error }),
      ),
    );
  }

  private creationOp(
    key: string,
    sadmEntityId: number,
    answer: FormAnswer,
    name: string,
  ): Observable<SadmEntityChangesetPartial> {
    return this.sadmHttp.updateSadmEntity(sadmEntityId, { name, entityCreationFormAnswer: answer }).pipe(
      map((sadmEntity: SadmEntity): SadmEntityChangesetPartial => ({
        action: 'creation',
        success: true,
        key,
        answer,
        sadmEntity,
      })),
      catchError((error: unknown): Observable<SadmEntityChangesetPartial> =>
        of({ action: 'creation', success: false, key, error }),
      ),
    );
  }

  private protocolOp(
    key: string,
    sadmEntityId: number,
    sadmClientId: number,
    answer: FormAnswer,
  ): Observable<SadmEntityChangesetPartial> {
    return this.sadmHttp.createProtocol({ sadmEntityId, sadmClientId, answers: answer.answers }).pipe(
      map((): SadmEntityChangesetPartial => ({ action: 'protocol', success: true, key, answer })),
      catchError((error: unknown): Observable<SadmEntityChangesetPartial> =>
        of({ action: 'protocol', success: false, key, error }),
      ),
    );
  }

  private analysisOps(
    key: string,
    sadmEntityId: number,
    analysis: SadmEntityAnalysisChangeset,
    patient: Patient,
  ): Observable<SadmEntityChangesetPartial> {
    const clientCode = patient.clientCode ?? '';
    // TODO(cross-sequence-medias): drop the clone step when backend supports attaching existing Media to a different wound
    const resolvedFiles$: Observable<File[]> = analysis.addedMedias.length
      ? forkJoin(analysis.addedMedias.map(cloneMediaAsFile)).pipe(map((cloned) => [...analysis.files, ...cloned]))
      : of([...analysis.files]);

    return resolvedFiles$.pipe(
      mergeMap((files) => {
        if (analysis.sequenceId !== undefined) {
          const sequenceId = analysis.sequenceId;
          const ops: Observable<SadmEntityChangesetPartial>[] = [
            this.editSequenceAnalysisOp(key, clientCode, sequenceId, analysis.answer),
          ];
          if (files.length) {
            ops.push(this.attachMediaOp(key, sadmEntityId, clientCode, sequenceId, files));
          }
          for (const mediaId of analysis.removedMediaIds) {
            ops.push(this.deleteMediaOp(key, clientCode, sequenceId, mediaId));
          }
          return merge(...ops);
        }

        return this.createSequenceOp(key, sadmEntityId, clientCode, patient, analysis.answer, files);
      }),
    );
  }

  private editSequenceAnalysisOp(
    key: string,
    clientCode: string,
    sequenceId: number,
    answer: FormAnswer,
  ): Observable<SadmEntityChangesetPartial> {
    return this.sequenceHttp.editSequenceAnalysis({ clientCode, sequenceId, formAnswer: answer }).pipe(
      map((updatedSequence): SadmEntityChangesetPartial => ({
        action: 'sequence-update',
        success: true,
        key,
        sequence: updatedSequence,
      })),
      catchError((error: unknown): Observable<SadmEntityChangesetPartial> =>
        of({ action: 'sequence-update', success: false, key, sequenceId, error }),
      ),
    );
  }

  private createSequenceOp(
    key: string,
    sadmEntityId: number,
    clientCode: string,
    patient: Patient,
    answer: FormAnswer,
    files: File[],
  ): Observable<SadmEntityChangesetPartial> {
    const formData = this.buildCreateSequenceFormData(sadmEntityId, patient, answer);
    return this.sequenceHttp.createSequence(formData).pipe(
      mergeMap((sequence) => {
        const created: SadmEntityChangesetPartial = { action: 'sequence-create', success: true, key, sequence };
        if (!files.length) {
          return of(created);
        }
        return concat(
          of(created),
          this.attachMediaOp(key, sadmEntityId, clientCode, sequence.sequenceInstance.id, files),
        );
      }),
      catchError((error: unknown): Observable<SadmEntityChangesetPartial> =>
        of({ action: 'sequence-create', success: false, key, error }),
      ),
    );
  }

  private attachMediaOp(
    key: string,
    sadmEntityId: number,
    clientCode: string,
    sequenceId: number,
    files: readonly File[],
  ): Observable<SadmEntityChangesetPartial> {
    return this.sequenceHttp.attachSequenceMedias(clientCode, sequenceId, [...files]).pipe(
      map((medias): SadmEntityChangesetPartial => ({
        action: 'medias-attach',
        success: true,
        key,
        sadmEntityId,
        sequenceId,
        medias,
        sourceFiles: files,
      })),
      catchError((error: unknown): Observable<SadmEntityChangesetPartial> =>
        of({ action: 'medias-attach', success: false, key, sequenceId, sourceFiles: files, error }),
      ),
    );
  }

  private deleteMediaOp(
    key: string,
    clientCode: string,
    sequenceId: number,
    mediaId: number,
  ): Observable<SadmEntityChangesetPartial> {
    return this.sequenceHttp.deleteSequenceMedia(clientCode, sequenceId, mediaId).pipe(
      map((): SadmEntityChangesetPartial => ({ action: 'media-remove', success: true, key, sequenceId, mediaId })),
      catchError((error: unknown): Observable<SadmEntityChangesetPartial> =>
        of({ action: 'media-remove', success: false, key, sequenceId, mediaId, error }),
      ),
    );
  }

  private buildCreateSequenceFormData(sadmEntityId: number, patient: Patient, analysisAnswer: FormAnswer): FormData {
    const data = new FormData();
    data.append('clientCode', patient.clientCode ?? '');
    data.append('createdOn', formatToIso(new Date()));
    appendPatientToCreateSequenceFormData(data, patient);
    data.append('sadmEntityId', String(sadmEntityId));
    data.append('formAnswer', JSON.stringify(analysisAnswer));
    return data;
  }
}
